import React, { useState } from 'react';
import { PlotSizingResults } from '../../../utils/PlotSizingCalculator';
import './PlotVisualizer.scss';

interface PlotVisualizerFormValues {
  executionEquipmentSwathWidth?: number;
  executionEquipmentSwathWidthUnits?: string;
  harvesterSwathWidth?: number;
  harvesterSwathWidthUnits?: string;
  maxLikelyHarvestAngle?: number;
}

interface PlotVisualizerProps {
  results: PlotSizingResults;
  formValues: PlotVisualizerFormValues;
}

const PlotVisualizer: React.FC<PlotVisualizerProps> = ({
  results,
  formValues,
}) => {
  const [showSplitFilled, setShowSplitFilled] = useState(false);

  // Calculate layout dimensions
  const targetFieldWidthFt = 500; // Preferred field width
  const plotTotalWidthFt = results.suggestedPlotWidthFt;
  const plotTotalLengthFt = results.suggestedPlotLengthFt;
  const fieldBufferFt = results.suggestedFieldBufferWidthFt;

  // Calculate how many plots fit in target width, ensure minimum of 2
  const plotsInTargetWidth = Math.floor(targetFieldWidthFt / plotTotalWidthFt);
  const numPlots = Math.max(2, plotsInTargetWidth);

  // Adjust field width to accommodate the desired number of plots
  const actualFieldWidthFt = numPlots * plotTotalWidthFt + fieldBufferFt * 2;
  const totalFieldLengthFt = plotTotalLengthFt; // No top/bottom field buffers needed

  // SVG dimensions and scale
  const svgWidth = 750;
  const svgHeight = 500;
  const scaleX = svgWidth / actualFieldWidthFt;
  const scaleY = svgHeight / totalFieldLengthFt;
  const scale = Math.min(scaleX, scaleY) * 0.9; // Leave some margin

  // Convert feet to SVG coordinates
  const toSvgX = (ft: number) =>
    ft * scale + (svgWidth - actualFieldWidthFt * scale) / 2;
  const toSvgY = (ft: number) =>
    ft * scale + (svgHeight - totalFieldLengthFt * scale) / 2;
  const toSvgWidth = (ft: number) => ft * scale;
  const toSvgHeight = (ft: number) => ft * scale;

  // Equipment dimensions - convert to feet if needed
  const convertToFeet = (value: number, units: string) => {
    return units === 'm' ? value / 0.3048 : value;
  };

  const executionSwathFt =
    results.effectiveExecutionWidthFt ||
    convertToFeet(
      formValues?.executionEquipmentSwathWidth || 30,
      formValues?.executionEquipmentSwathWidthUnits || 'ft',
    );

  const harvesterSwathFt = convertToFeet(
    formValues?.harvesterSwathWidth || 25,
    formValues?.harvesterSwathWidthUnits || 'ft',
  );
  const maxHarvestAngle = formValues?.maxLikelyHarvestAngle || 0;

  return (
    <div className="plot-visualizer">
      <h3 className="plot-sizing-calculator__section-title">
        Field Layout Visualization
      </h3>

      <div className="plot-visualizer__description">
        <div className="plot-visualizer__description-text">
          Showing {numPlots} plots across {actualFieldWidthFt.toFixed(0)} ft
          field width
        </div>

        <div className="plot-visualizer__description-checkbox">
          <label>
            <input
              type="checkbox"
              checked={showSplitFilled}
              onChange={(e) => setShowSplitFilled(e.target.checked)}
            />
            Show filled equipment passes (split view)
          </label>
        </div>
      </div>

      <svg width={svgWidth} height={svgHeight} className="plot-visualizer__svg">
        {/* Define pattern for plot buffer hatching */}
        <defs>
          <pattern
            id="plotBufferHatch"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
          >
            <path
              d="M0,6 l6,-6 M-1,1 l2,-2 M5,7 l2,-2"
              stroke="#2196f3"
              strokeWidth="1"
            />
          </pattern>
        </defs>

        {/* Field background */}
        <rect
          x={toSvgX(0)}
          y={toSvgY(0)}
          width={toSvgWidth(actualFieldWidthFt)}
          height={toSvgHeight(totalFieldLengthFt)}
          fill="#f1f8e9"
          stroke="#124f43"
          strokeWidth="2"
        />

        {/* Field buffer zones (sides only) */}
        <rect
          x={toSvgX(0)}
          y={toSvgY(0)}
          width={toSvgWidth(fieldBufferFt)}
          height={toSvgHeight(totalFieldLengthFt)}
          fill="#e3f2fd"
          fillOpacity="0.7"
        />
        <rect
          x={toSvgX(actualFieldWidthFt - fieldBufferFt)}
          y={toSvgY(0)}
          width={toSvgWidth(fieldBufferFt)}
          height={toSvgHeight(totalFieldLengthFt)}
          fill="#e3f2fd"
          fillOpacity="0.7"
        />

        {/* Individual plots with equipment passes */}
        {Array.from({ length: numPlots }, (_, i) => {
          const plotStartX = fieldBufferFt + i * plotTotalWidthFt;

          return (
            <g key={i}>
              {/* Plot background */}
              <rect
                x={toSvgX(plotStartX)}
                y={toSvgY(0)}
                width={toSvgWidth(plotTotalWidthFt)}
                height={toSvgHeight(plotTotalLengthFt)}
                fill="none"
                stroke="#888"
                strokeWidth="0.5"
                strokeDasharray="2,2"
              />
            </g>
          );
        })}

        {showSplitFilled ? (
          // Split filled view
          <>
            {/* Execution equipment (top half) - continuous across field */}
            {Array.from(
              {
                length: Math.ceil(
                  (actualFieldWidthFt - fieldBufferFt * 2) / executionSwathFt,
                ),
              },
              (_, passIndex) => {
                const passX = fieldBufferFt + passIndex * executionSwathFt;
                const passWidth = Math.min(
                  executionSwathFt,
                  actualFieldWidthFt - fieldBufferFt - passX,
                );

                if (passWidth > 0) {
                  return (
                    <rect
                      key={`exec-split-${passIndex}`}
                      x={toSvgX(passX)}
                      y={toSvgY(0)}
                      width={toSvgWidth(passWidth)}
                      height={toSvgHeight(plotTotalLengthFt * 0.5)}
                      fill="#fff3cd"
                      stroke="#ffc107"
                      strokeWidth="0.5"
                      fillOpacity="0.7"
                    />
                  );
                }
                return null;
              },
            )}

            {/* Harvester (bottom half) - continuous across field */}
            {Array.from(
              {
                length: Math.ceil(
                  (actualFieldWidthFt - fieldBufferFt * 2) /
                    (harvesterSwathFt *
                      Math.cos((maxHarvestAngle * Math.PI) / 180)),
                ),
              },
              (_, passIndex) => {
                const angleRadians = (maxHarvestAngle * Math.PI) / 180;
                const adjustedSwathSpacing =
                  harvesterSwathFt * Math.cos(angleRadians);
                const passX = fieldBufferFt + passIndex * adjustedSwathSpacing;
                const passWidth = Math.min(
                  adjustedSwathSpacing,
                  actualFieldWidthFt - fieldBufferFt - passX,
                );

                const centerX = toSvgX(passX + passWidth / 2);
                const centerY = toSvgY(plotTotalLengthFt * 0.75);

                if (passWidth > 0) {
                  return (
                    <g key={`harv-split-${passIndex}`}>
                      <rect
                        x={toSvgX(passX)}
                        y={toSvgY(plotTotalLengthFt * 0.5)}
                        width={toSvgWidth(passWidth)}
                        height={toSvgHeight(plotTotalLengthFt * 0.5)}
                        fill="#ffebee"
                        stroke="#dc3545"
                        strokeWidth="0.5"
                        fillOpacity="0.8"
                        transform={`rotate(${maxHarvestAngle} ${centerX} ${centerY})`}
                      />
                    </g>
                  );
                }
                return null;
              },
            )}
          </>
        ) : (
          // Outline whole plot view
          <>
            {/* Execution equipment outlines - continuous across field */}
            {Array.from(
              {
                length: Math.ceil(
                  (actualFieldWidthFt - fieldBufferFt * 2) / executionSwathFt,
                ),
              },
              (_, passIndex) => {
                const passX = fieldBufferFt + passIndex * executionSwathFt;
                const passWidth = Math.min(
                  executionSwathFt,
                  actualFieldWidthFt - fieldBufferFt - passX,
                );

                if (passWidth > 0) {
                  return (
                    <rect
                      key={`exec-outline-${passIndex}`}
                      x={toSvgX(passX)}
                      y={toSvgY(0)}
                      width={toSvgWidth(passWidth)}
                      height={toSvgHeight(plotTotalLengthFt)}
                      fill="none"
                      stroke="#ffc107"
                      strokeWidth="1"
                    />
                  );
                }
                return null;
              },
            )}

            {/* Harvester outlines */}
            {Array.from(
              {
                length: Math.ceil(
                  (actualFieldWidthFt - fieldBufferFt * 2) /
                    (harvesterSwathFt *
                      Math.cos((maxHarvestAngle * Math.PI) / 180)),
                ),
              },
              (_, j) => {
                const angleRadians = (maxHarvestAngle * Math.PI) / 180;
                const adjustedSwathSpacing =
                  harvesterSwathFt * Math.cos(angleRadians);
                const passX = fieldBufferFt + j * adjustedSwathSpacing;
                const passWidth = Math.min(
                  adjustedSwathSpacing,
                  actualFieldWidthFt -
                    fieldBufferFt * 2 -
                    j * adjustedSwathSpacing,
                );

                const centerX = toSvgX(passX + passWidth / 2);
                const centerY = toSvgY(plotTotalLengthFt * 0.5);

                if (passWidth > 0) {
                  return (
                    <g key={`harv-outline-${j}`}>
                      <rect
                        x={toSvgX(passX)}
                        y={toSvgY(0)}
                        width={toSvgWidth(passWidth)}
                        height={toSvgHeight(plotTotalLengthFt)}
                        fill="none"
                        stroke="#dc3545"
                        strokeWidth="1"
                        strokeDasharray="3,3"
                        transform={`rotate(${maxHarvestAngle} ${centerX} ${centerY})`}
                      />
                    </g>
                  );
                }
                return null;
              },
            )}
          </>
        )}

        {/* Plot buffer zones - drawn as overlays on top */}
        {Array.from({ length: numPlots }, (_, i) => {
          const plotStartX = fieldBufferFt + i * plotTotalWidthFt;

          return (
            <g key={`buffers-${i}`}>
              {/* Plot buffer zones - top (middle section only) */}
              <rect
                x={toSvgX(plotStartX + results.suggestedPlotBufferWidthFt)}
                y={toSvgY(0)}
                width={toSvgWidth(
                  plotTotalWidthFt - 2 * results.suggestedPlotBufferWidthFt,
                )}
                height={toSvgHeight(results.suggestedPlotBufferLengthFt)}
                fill="url(#plotBufferHatch)"
                fillOpacity="0.5"
              />

              {/* Plot buffer zones - bottom (middle section only) */}
              <rect
                x={toSvgX(plotStartX + results.suggestedPlotBufferWidthFt)}
                y={toSvgY(
                  plotTotalLengthFt - results.suggestedPlotBufferLengthFt,
                )}
                width={toSvgWidth(
                  plotTotalWidthFt - 2 * results.suggestedPlotBufferWidthFt,
                )}
                height={toSvgHeight(results.suggestedPlotBufferLengthFt)}
                fill="url(#plotBufferHatch)"
                fillOpacity="0.5"
              />

              {/* Plot buffer zones - left */}
              <rect
                x={toSvgX(plotStartX)}
                y={toSvgY(0)}
                width={toSvgWidth(results.suggestedPlotBufferWidthFt)}
                height={toSvgHeight(plotTotalLengthFt)}
                fill="url(#plotBufferHatch)"
                fillOpacity="0.5"
              />

              {/* Plot buffer zones - right */}
              <rect
                x={toSvgX(
                  plotStartX +
                    plotTotalWidthFt -
                    results.suggestedPlotBufferWidthFt,
                )}
                y={toSvgY(0)}
                width={toSvgWidth(results.suggestedPlotBufferWidthFt)}
                height={toSvgHeight(plotTotalLengthFt)}
                fill="url(#plotBufferHatch)"
                fillOpacity="0.5"
              />
            </g>
          );
        })}

        {/* Plot boundaries drawn on top of everything */}
        {Array.from({ length: numPlots }, (_, i) => {
          const plotStartX = fieldBufferFt + i * plotTotalWidthFt;

          return (
            <g key={`boundaries-${i}`}>
              {/* Complete plot boundary */}
              <rect
                x={toSvgX(plotStartX)}
                y={toSvgY(0)}
                width={toSvgWidth(plotTotalWidthFt)}
                height={toSvgHeight(plotTotalLengthFt)}
                fill="none"
                stroke="#124f43"
                strokeWidth="1"
              />

              {/* Usable plot area (visual indicator only) */}
              <rect
                x={toSvgX(plotStartX + results.suggestedPlotBufferWidthFt)}
                y={toSvgY(results.suggestedPlotBufferLengthFt)}
                width={toSvgWidth(
                  plotTotalWidthFt - 2 * results.suggestedPlotBufferWidthFt,
                )}
                height={toSvgHeight(
                  plotTotalLengthFt - 2 * results.suggestedPlotBufferLengthFt,
                )}
                fill="none"
                stroke="#124f43"
                strokeWidth="3"
              />
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="plot-visualizer__legend">
        <div className="plot-visualizer__legend-item plot-visualizer__legend-item--usable">
          <div className="plot-visualizer__legend-item-color"></div>
          <span className="plot-visualizer__legend-item-label">
            Usable Plot Area
          </span>
        </div>
        <div className="plot-visualizer__legend-item plot-visualizer__legend-item--buffer">
          <div className="plot-visualizer__legend-item-color"></div>
          <span className="plot-visualizer__legend-item-label">
            Plot Buffer Zones
          </span>
        </div>
        <div className="plot-visualizer__legend-item plot-visualizer__legend-item--field-buffer">
          <div className="plot-visualizer__legend-item-color"></div>
          <span className="plot-visualizer__legend-item-label">
            Field Buffer (sides only)
          </span>
        </div>
        <div
          className={`plot-visualizer__legend-item plot-visualizer__legend-item--execution ${
            showSplitFilled ? 'filled' : ''
          }`}
        >
          <div className="plot-visualizer__legend-item-color"></div>
          <span className="plot-visualizer__legend-item-label">
            Execution Equipment ({executionSwathFt}' swath)
          </span>
        </div>
        <div
          className={`plot-visualizer__legend-item plot-visualizer__legend-item--harvester ${
            showSplitFilled ? 'filled' : ''
          }`}
        >
          <div className="plot-visualizer__legend-item-color"></div>
          <span className="plot-visualizer__legend-item-label">
            Harvester ({harvesterSwathFt}' swath)
          </span>
        </div>
      </div>
    </div>
  );
};

export default PlotVisualizer;
